import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { authAPI } from '../services/api';
import apiService from '../services/apiService';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'technician' | 'client';
  avatar?: string;
  phone?: string;
  isActive?: boolean;
  address?: string;
  city?: string;
  postalCode?: string;
  departements: string[];
  lastLogin?: string;
  availability?: {
    status: 'available' | 'unavailable';
    startDate?: Date | null;
    endDate?: Date | null;
  };
  companyId?: string; // Added companyId
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
  }, []);

  useEffect(() => {
      const verifyAuth = async () => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          try {
            const response = await authAPI.verifyToken();
            const userData = response.data.data.user;
            setUser(userData);
            localStorage.setItem('user_data', JSON.stringify(userData));
          } catch (error: any) {
            const status = error?.response?.status;
            // ⬇️ NE déconnecte que si c'est vraiment un problème d'auth
            if (status === 401 || status === 403) {
              logout();
            } else {
              console.warn('verifyAuth: non-auth error, keep current user', error);
              // on garde `user` tel quel pour éviter un flip d’UI
            }
          }
        }
        setIsLoading(false);
      };

      verifyAuth();
    }, [logout]);


  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await authAPI.login(email, password);
      const { accessToken, user: userData } = response.data.data;

      localStorage.setItem('auth_token', accessToken);
      localStorage.setItem('user_data', JSON.stringify(userData));
      setUser(userData);
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.message || 'Erreur de connexion. Vérifiez vos identifiants.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
  try {
      const response = await apiService.auth.getProfile();
      const updatedUser = response.data;
      setUser(updatedUser);
      localStorage.setItem('user_data', JSON.stringify(updatedUser));
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        logout();
      } else {
        console.warn('refreshUser: non-auth error, ignore', error);
      }
    }
  }, [logout]);

  const value = useMemo(() => ({
    user,
    login,
    logout,
    isLoading,
    isAuthenticated: !!user,
    refreshUser,
  }), [user, isLoading, login, logout, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
